import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetShowcaseCategories, GetShowcases, GetShowcaseItems } from "../../../api/queries";
import { ShowcaseCategory, Showcase, ShowcaseItem } from "../../../api/zod-types";
import CategoryCard from "../CategoryCard";
import ProductNotFound from "../ProductNotFound";
import CTAExploreMore from "../CTAExploreMore";
import { SafeLink } from "@/components/ui/SafeLink";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";


function slugify(str: string) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function CategoryShowcaseGrid() {
   
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    function handlePrefetchSubCategory(subCategoryName: string) {
        queryClient.prefetchQuery({
            queryKey: ["showcases", subCategoryName],
            queryFn: GetShowcases
        });
        queryClient.prefetchQuery({
            queryKey: ["showcaseitems", subCategoryName],
            queryFn: GetShowcaseItems
        });
    }

    const { category: categoryId } = useParams<{ category: string }>();

    const { data, isLoading, isError } = useQuery({
        queryKey: ["showcasecategories"],
        queryFn: GetShowcaseCategories,
        staleTime: 5 * 60 * 1000,
    });
    const { data: showcasesData } = useQuery({
        queryKey: ["showcases"],
        queryFn: GetShowcases,
        staleTime: 5 * 60 * 1000,
    });
    const { data: showcaseItemsData } = useQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems,
        staleTime: 5 * 60 * 1000,
    });

    let categories: ShowcaseCategory[] = [];
    let parentCategory: ShowcaseCategory | undefined;
    let level1Categories: ShowcaseCategory[] = [];
    if (data?.data) {
        let parsed = typeof data.data === "string" ? JSON.parse(data.data) : data.data;
        categories = parsed?.results || [];
        parentCategory = categories.find(
            cat => slugify(cat.categoryname) === categoryId && cat.categorylevel === "0"
        );
        if (parentCategory) {
            level1Categories = categories.filter(
                cat => cat.categorylevel === "1" && cat.categorytag === (parentCategory?.categoryname ?? "")
            );
        }
    }

    let showcases: Showcase[] = [];
    let showcaseItems: ShowcaseItem[] = [];
    if (showcasesData?.data) {
        showcases = Array.isArray(showcasesData.data) ? showcasesData.data : (showcasesData.data.results || []);
    }
    if (showcaseItemsData?.data) {
        showcaseItems = Array.isArray(showcaseItemsData.data) ? showcaseItemsData.data : (showcaseItemsData.data.results || []);
    }

    function getProductCountForLevel1Category(categoryName: string): number {
        const showcasesForCat = showcases.filter(
            s => s.showcasecategoryname && s.showcasecategoryname.trim().toLowerCase() === categoryName.trim().toLowerCase()
        );
        const showcaseNames = showcasesForCat.map(s => s.showcasename);
        return showcaseItems.filter(item => showcaseNames.includes(item.showcasename)).length;
    }
    //only count unique products
    function getUniqueProductCountForLevel1Category(categoryName: string): number {
        const showcasesForCat = showcases.filter(
            s => s.showcasecategoryname && s.showcasecategoryname.trim().toLowerCase() === categoryName.trim().toLowerCase()
        );
        const showcaseNames = showcasesForCat.map(s => s.showcasename);
        // Deduplicate by productid
        const uniqueProducts = showcaseItems
            .filter(item => showcaseNames.includes(item.showcasename))
            .reduce((acc, curr) => {
                acc[curr.productid] = true;
                return acc;
            }, {} as Record<string, boolean>);
        return Object.keys(uniqueProducts).length;
    }

    if (isLoading) {
        return <div className="text-center py-12 text-lg text-gray-500">Loading...</div>;
    }
    if (isError || !parentCategory) {
        return <ProductNotFound />;
    }

    // If no level 1 categories, show ProductNotFound and ExploreMore only
    if (level1Categories.length === 0) {
        return (
            <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen px-2 justify-center items-center">
                <div className="max-w-lg w-full mx-auto">
                    <ProductNotFound onGoBack={() => navigate("/products")} />
                </div>
            </div>
        );
    }
    // Sort level1Categories alphabetically by categoryname
    level1Categories = [...level1Categories].sort((a, b) =>
        a.categoryname.localeCompare(b.categoryname)
    );

    // Breadcrumb
    const breadcrumbs = (
        <nav className="text-sm mb-6" aria-label="Breadcrumb">
            <ol className="flex flex-wrap gap-2 text-gray-500">
            <li >
                <SafeLink className="hover:underline   " to="/">Home</SafeLink>                
            </li>
            <li>/</li>
            <li >
                <SafeLink className="hover:underline  " to="/products">Products</SafeLink>                
            </li>
            <li>/</li>
            {parentCategory?.categoryname && (
                <li className="font-semibold text-black">
                    {parentCategory.categoryname}
                </li>
            )}
        </ol>
        </nav>
    );


    return (
        <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen ">
            {/* <div className="w-full max-w-4xl mx-auto mt-10 mb-8">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center mb-4 tracking-tight capitalize">
                    {parentCategory?.categoryname ?? ""}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 text-center mb-6">
                    Discover our range of {parentCategory?.categoryname?.toLowerCase() ?? ""} categories.
                </p>
            </div> */}
            
            {/* Hero Section - full width, background image, overlay */}
            <section
                className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center mb-8"
                style={
                    parentCategory?.categoryimageurl
                        ? {
                            backgroundImage: `url(${parentCategory.categoryimageurl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }
                        : {
                            backgroundColor: "#111",
                        }
                }
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
                {/* Decorative elements (optional) */}
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-60"></div>
                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse opacity-80"></div>
                {/* Main hero content */}
                <div className="relative z-10 flex flex-col items-center justify-center w-full h-full text-center px-4">
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold py-4 text-white animate-fade-in-up delay-200">
                        {parentCategory?.categoryname ?? "Category"}
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 py-4 animate-fade-in-up delay-400 max-w-2xl mx-auto">
                        Explore our premium selection of {parentCategory?.categoryname?.toLowerCase() ?? "products"} products. Find the perfect product for your needs.
                    </p>
                </div>
            </section>
            {/* Breadcrumbs */}
            <div className="w-full max-w-6xl mx-auto mt-4 px-4 md:px-8 lg:px-16 xl:px-32  mb-8">
                {breadcrumbs}
            </div>
            <div className="w-full max-w-6xl mx-auto mt-4 px-4 md:px-8 lg:px-16 xl:px-32  mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-2 md:px-4 lg:px-8 xl:px-12">
                    {level1Categories
                    .filter(cat => getProductCountForLevel1Category(cat.categoryname) > 0)
                    .map((cat, idx) => (
                        <CategoryCard
                            key={cat.categoryid}
                            title={cat.categoryname}
                            image={cat.categoryimageurl}
                            url={`/products/${slugify(parentCategory?.categoryname ?? "")}/${slugify(cat.categoryname)}`}
                            onClick={() => navigate(`/products/${slugify(parentCategory?.categoryname ?? "")}/${slugify(cat.categoryname)}`)}
                            onMouseEnter={() => handlePrefetchSubCategory(cat.categoryname)}
                            productCount={getProductCountForLevel1Category(cat.categoryname)}
                            // productCount={getUniqueProductCountForLevel1Category(cat.categoryname)}
                            animateClassName="animate-fade-in-up"
                            animationDelay={`${idx * 100}ms`}
                        />
                    ))}
                </div>
                <div className="flex justify-center mt-8">
                    <CTAExploreMore
                        text="Main Product Catalogue"
                        action="Back to"
                        suffix=""
                        to="/products"
                    />
                </div>

            </div>
        </div>
    );
}